import React, { useEffect, useState, useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useTherapistSubscription } from "@/hooks/useTherapistSubscription";
import { THERAPIST_BILLING_ENABLED } from "@/lib/therapistBilling";


// Maximum time to wait for subscription check before timing out
const SUBSCRIPTION_CHECK_TIMEOUT_MS = 10000;

interface TherapistProtectedRouteProps {
  children: React.ReactNode;
}

const TherapistProtectedRoute: React.FC<TherapistProtectedRouteProps> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [isTherapist, setIsTherapist] = useState(false);
  const [subscriptionTimedOut, setSubscriptionTimedOut] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { isSubscribed, loading: subscriptionLoading, error: subscriptionError } = useTherapistSubscription();

  // Timeout for subscription loading to prevent infinite white screen
  useEffect(() => {
    if (subscriptionLoading && !subscriptionTimedOut) {
      timeoutRef.current = setTimeout(() => {
        console.warn('TherapistProtectedRoute: Subscription check timed out');
        setSubscriptionTimedOut(true);
      }, SUBSCRIPTION_CHECK_TIMEOUT_MS);
    }
    
    // Clear timeout if loading completes
    if (!subscriptionLoading && timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [subscriptionLoading, subscriptionTimedOut]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (authLoading) return;
      if (!user) {
        if (!cancelled) setChecking(false);
        return;
      }

      try {
        const { data } = await supabase
          .from("therapist_profiles")
          .select("id")
          .eq("user_id", user.id)
          .maybeSingle();

        if (!cancelled) {
          setIsTherapist(Boolean(data?.id));
          setChecking(false);
        }
      } catch {
        if (!cancelled) {
          setIsTherapist(false);
          setChecking(false);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [user, authLoading]);

  // Determine if we should stop waiting for subscription
  // Stop waiting if: loading finished, error occurred, or timeout reached
  const shouldStopWaiting = !subscriptionLoading || subscriptionError || subscriptionTimedOut;

  // Return null instead of LoadingAnimation - the NativeSplashGate covers this
  // But don't wait forever - if timed out or errored, proceed to show subscription required
  if (authLoading || checking || (subscriptionLoading && !shouldStopWaiting)) return null;

  // Not authenticated - redirect to therapist auth
  if (!user) {
    return (
      <Navigate
        to={`/therapist-auth?tab=signin&next=${encodeURIComponent(location.pathname + location.search)}`}
        replace
      />
    );
  }

  // Not a therapist - redirect to therapist signup/auth
  if (!isTherapist) {
    return <Navigate to="/therapist-auth?tab=signin" replace />;
  }

  if (THERAPIST_BILLING_ENABLED && !isSubscribed) {
    return <Navigate to="/therapist-subscription-required" replace />;
  }


  return <>{children}</>;
};

export default TherapistProtectedRoute;
